import { getModel } from '../../model/registry'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import { useStore } from '../pageView/Page'
import { evaluateCondition } from '../../../../common/model/condition/evaluate'
import { icon } from '../../styles/icons'

import DocumentPreview from './DocumentPreview'
import { Wrapper, Bar, Tabs, Tab, Spacer, IconButton, DropZone, ErrorMsg, fileTypeOf } from './DocumentNavigator'

/*
 * The documents of one line — a receipt behind a journal line. The same
 * component is the desktop side panel (for the line picked in the grid or the
 * card) and, on mobile, the modal a line's documents button opens.
 *
 * The documents live on the line (`lines[i].attachments`), so they move with
 * it when lines are reordered and go with it when it is removed.
 *
 * A draft takes uploads and removals into the form like any other edit, saved
 * with the record. A locked record (a posted journal) writes straight away:
 * it still takes new documents, and removing one only marks it deleted. A
 * deleted document is hidden until "Show deleted" brings it back into view —
 * it is never let go of, because the books it backs can no longer change.
 */

const LineTitle = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Hint = styled.div`
  color: ${({ theme }) => theme.font.label};
  font-size: 0.875rem;
`

const DeletedTab = styled(Tab)`
  text-decoration: line-through;
`

const ToggleDeleted = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.tab.navLinkColor};
  font-size: 0.8125rem;
  cursor: pointer;
`

const DeletedNote = styled.div`
  color: ${({ theme }) => theme.btn.danger};
  font-size: 0.8125rem;
`

const formatDate = (iso) => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
}

const LineDocuments = ({ lineDocuments, index, entity }) => {
  const config = getModel('lineDocumentsData')[lineDocuments] ?? {}
  const linesArray = config.linesArray ?? 'lines'

  const [state, setState, getState] = useStore(s => s)
  const { postMeForm, putMe } = useApi()
  const { t, m } = useTranslation()
  const queryClient = useQueryClient()
  const inputRef = useRef(null)

  const [selected, setSelected] = useState(null)
  const [showDeleted, setShowDeleted] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const lines = Array.isArray(state?.[linesArray]) ? state[linesArray] : []
  const line = index !== null && index !== undefined ? lines[index] : undefined
  const attachments = Array.isArray(line?.attachments) ? line.attachments : []
  const deletedCount = attachments.filter(a => a.deletedAt).length
  const documents = attachments.filter(a => showDeleted || !a.deletedAt)
  const locked = config.locked ? evaluateCondition(config.locked.condition, { values: state }) : false
  const target = entity || state?.entity

  // Keep the selection on a document that is shown; fall back to the first.
  const current = documents.find(d => d.fileUrl === selected) ?? documents[0]
  useEffect(() => {
    if (current && current.fileUrl !== selected) setSelected(current.fileUrl)
  }, [current?.fileUrl])

  // Another line, another set of documents: start again from its first.
  useEffect(() => { setSelected(null); setError(null) }, [index])

  // Read the state afresh: an upload finishes after other edits may have landed.
  const saveAttachments = async (change) => {
    const fresh = getState()
    const freshLines = [...(Array.isArray(fresh?.[linesArray]) ? fresh[linesArray] : [])]
    if (!freshLines[index]) return
    const next = change(freshLines[index].attachments ?? [])
    freshLines[index] = { ...freshLines[index], attachments: next }

    if (locked && fresh.id) {
      const response = await putMe(target, { [linesArray]: freshLines }, fresh.id)
      if (!response || response.error) throw new Error(response?.error || 'update failed')
      // The server stamps a deletion with its own time and user.
      setState({ [linesArray]: Array.isArray(response[linesArray]) ? response[linesArray] : freshLines })
      await queryClient.invalidateQueries({ queryKey: [target] })
    } else {
      setState({ [linesArray]: freshLines, savedOn: new Date() })
    }
  }

  const upload = async (files) => {
    setError(null)
    const list = Array.from(files ?? [])
    if (list.length === 0) return
    if (list.some(file => !fileTypeOf(file))) {
      setError(t('document.navigator.error.type'))
      return
    }
    setUploading(true)
    try {
      const added = []
      for (const file of list) {
        const response = await postMeForm('file', file, { folder: config.folder || 'documents' })
        const record = response?.data?.record
        if (!record) throw new Error('upload failed')
        added.push({
          fileUrl: record.id,
          fileName: record.fileName || file.name,
          fileType: fileTypeOf(file),
          fileSize: file.size,
          uploadedAt: new Date().toISOString()
        })
      }
      // The same file twice on one line is one document.
      await saveAttachments(existing => [
        ...existing,
        ...added.filter(doc => !existing.some(e => e.fileUrl === doc.fileUrl && !e.deletedAt))
      ])
      setSelected(added[added.length - 1].fileUrl)
    } catch {
      setError(t('document.navigator.error.upload'))
    } finally {
      setUploading(false)
    }
  }

  const remove = async () => {
    if (!current || current.deletedAt) return
    setError(null)
    try {
      await saveAttachments(existing => locked
        ? existing.map(a => a.fileUrl === current.fileUrl ? { ...a, deletedAt: new Date().toISOString() } : a)
        : existing.filter(a => a.fileUrl !== current.fileUrl))
    } catch {
      setError(t('document.navigator.error.remove'))
    }
  }

  const dropProps = {
    onDragOver: e => { e.preventDefault(); setDragging(true) },
    onDragLeave: () => setDragging(false),
    onDrop: e => { e.preventDefault(); setDragging(false); if (!uploading) upload(e.dataTransfer.files) }
  }

  if (!line) {
    return <Wrapper><Hint>{t('document.lines.noLine')}</Hint></Wrapper>
  }

  const lineLabel = config.lineLabel
    ? m(config.lineLabel, [index + 1, ...(config.lineLabelParams ?? []).map(p => line[p] ?? '')])
    : ''

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={config.accept}
      style={{ display: 'none' }}
      onChange={e => { upload(e.target.files); e.target.value = '' }}
    />
  )

  const deletedToggle = deletedCount > 0 && (
    <ToggleDeleted type="button" onClick={() => setShowDeleted(v => !v)}>
      {showDeleted ? t('document.lines.hideDeleted') : m('document.lines.showDeleted', [deletedCount])}
    </ToggleDeleted>
  )

  if (documents.length === 0) {
    return (
      <Wrapper {...dropProps}>
        {lineLabel && <LineTitle title={lineLabel}>{lineLabel}</LineTitle>}
        <DropZone $dragging={dragging} onClick={() => !uploading && inputRef.current?.click()}>
          {icon.upload}
          <span>{uploading ? t('document.navigator.uploading') : t('document.lines.empty')}</span>
          {fileInput}
        </DropZone>
        {deletedToggle}
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </Wrapper>
    )
  }

  return (
    <Wrapper {...dropProps}>
      {lineLabel && <LineTitle title={lineLabel}>{lineLabel}</LineTitle>}
      <Bar>
        {documents.length > 1 ? (
          <Tabs>
            {documents.map((doc, i) => {
              const label = doc.fileName || m('document.lines.uploaded', [i + 1])
              const TabKind = doc.deletedAt ? DeletedTab : Tab
              return (
                <TabKind key={doc.fileUrl} type="button" title={label}
                  className={doc.fileUrl === current?.fileUrl ? 'active' : ''}
                  onClick={() => setSelected(doc.fileUrl)}>
                  {label}
                </TabKind>
              )
            })}
          </Tabs>
        ) : <Spacer />}
        {current && !current.deletedAt && (
          <IconButton type="button" onClick={remove}
            title={locked ? t('document.lines.remove.locked') : t('document.navigator.remove')}>
            {icon.delete}
          </IconButton>
        )}
        <IconButton type="button" disabled={uploading} onClick={() => inputRef.current?.click()}>
          {icon.upload}
          <span>{uploading ? t('document.navigator.uploading') : t('document.navigator.upload')}</span>
        </IconButton>
        {fileInput}
      </Bar>
      {deletedToggle}
      {error && <ErrorMsg>{error}</ErrorMsg>}
      {current?.deletedAt && <DeletedNote>{m('document.lines.deletedOn', [formatDate(current.deletedAt)])}</DeletedNote>}
      {current && <DocumentPreview key={current.fileUrl} file={current} />}
    </Wrapper>
  )
}

export default LineDocuments
